import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import API_ENDPOINTS from '../config/api';
import SocketService from '../../../../packages/shared/socket.js';
import { createRefetchCoalescer } from './refetchCoalescer';
import {
  REFRESH_EVENTS, AI_CALL_EVENT, WORKSHOP_EVENT, POLL_INTERVAL_MS, MIN_REFETCH_GAP_MS,
} from './dashboardTokens';

// One fetch feeds every tile: API_ENDPOINTS.DASHBOARD (#1154) returns
// {flow, timer, moduleSummary, rag, cost, activity, schedule, chat} in a
// single round trip. Until #1154 lands on this branch the fetch fails and
// the screen shows its stale state — the real error, never mock data.
//
// Live updates are socket-first with a slow poll as fallback. The socket is
// connected before any subscription (wiki/pages/content-refresh-needs-a-socket.md),
// and event bursts coalesce into refetches ≥ MIN_REFETCH_GAP_MS apart.

const DashboardDataContext = createContext(null);

export function DashboardDataProvider({ instanceId, children }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  // Bumped on every ai-call-logged (#1155); the cost tile animates on change.
  const [aiPulse, setAiPulse] = useState(0);
  // Live workshop events since the last fetch (#1160, #1261). Every event
  // also triggers a coalesced refetch whose backfill covers these, so a
  // successful fetch clears them — the feed never shows the same event twice
  // for long.
  const [feedEvents, setFeedEvents] = useState([]);

  const fetchNow = useCallback(async () => {
    if (!instanceId) return;
    try {
      const response = await fetch(API_ENDPOINTS.DASHBOARD(instanceId), { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      setData(payload);
      setError(null);
      setLastUpdatedAt(Date.now());
      setFeedEvents([]);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  const fetchRef = useRef(fetchNow);
  fetchRef.current = fetchNow;

  useEffect(() => {
    if (!instanceId) return undefined;

    // Connect first, then subscribe — a subscription on a socket that never
    // connects hears nothing, silently.
    const socket = SocketService.connect(instanceId, 'display', `dashboard-${Date.now()}`);
    const coalescer = createRefetchCoalescer(() => fetchRef.current(), MIN_REFETCH_GAP_MS);

    const handlers = REFRESH_EVENTS.map((event) => {
      const handler = () => coalescer.request();
      socket.on(event, handler);
      return [event, handler];
    });

    // The feed hears workshop events only; the payload is the row the
    // backfill would return, so live and backfilled lines group alike.
    const onWorkshopEvent = (payload) => {
      if (!payload?.kind || !payload?.at) return;
      const { id, at, actor, kind, moduleId } = payload;
      setFeedEvents((prev) => [{ id, at, actor, kind, moduleId }, ...prev].slice(0, 60));
      coalescer.request();
    };
    socket.on(WORKSHOP_EVENT, onWorkshopEvent);

    // Every logged AI call pulses the cost tile; it makes no feed line.
    const onAiCall = () => {
      setAiPulse((n) => n + 1);
      coalescer.request();
    };
    socket.on(AI_CALL_EVENT, onAiCall);

    fetchRef.current();
    const poll = setInterval(() => fetchRef.current(), POLL_INTERVAL_MS);

    return () => {
      handlers.forEach(([event, handler]) => socket.off(event, handler));
      socket.off(WORKSHOP_EVENT, onWorkshopEvent);
      socket.off(AI_CALL_EVENT, onAiCall);
      clearInterval(poll);
      coalescer.dispose();
    };
  }, [instanceId]);

  const value = { data, error, loading, lastUpdatedAt, aiPulse, feedEvents, refetch: fetchNow };
  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useDashboardData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) throw new Error('useDashboardData must be used inside DashboardDataProvider');
  return ctx;
}
